import { Form, FormikProvider, useField, useFormik } from 'formik';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Route, useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { LabelType } from '@treams/treams-network';
import {
	ActionArchiveListItem,
	Button,
	Card,
	Combo,
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	Paragraph,
	QuillInput,
	QuillText,
	Select,
	SettingsRow,
	SettingsSection,
	Spinner,
	Switch,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import { StyrBadge } from '../../../connectors/products/StyrBadge';
import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import TooltipTarget from '../../../packages/kakapo-ui-kit/components/tooltips/TooltipTarget';
import validator from '../../../packages/kakapo-ui-services/factories/ValidatorFactory';
import CompetencyLevelSetModal from '../competency-level-sets/CompetencyLevelSetModal';

const JobTitleLink = styled(Link)`
	min-width: 0;
	color: ${tokens.text.primary};
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
`;

const DescriptionRow = styled.div`
	padding: ${tokens.padding.container}px;
	border-bottom: 1px solid ${tokens.border.subtle};

	&:last-child {
		border-bottom: 0;
	}
`;

function CompetencyLevel(props: {
	index: number;
	option: graphql.CompetencyLevelOption;
	borderless?: boolean;
}) {
	const { localize } = useLocalize();

	const [field, , { setValue }] = useField<boolean>(`options.${props.index}.enabled`);

	return (
		<SettingsRow borderless={props.borderless}>
			<Flex flexDirection="row" alignItems="center">
				<Box flexShrink={0}>
					<Switch
						label={localize(props.option.level.title).text}
						on={field.value}
						onChange={() => {
							setValue(!field.value);
						}}
					/>
				</Box>
				<Box ml={3} flexGrow={1}>
					{field.value && (
						<QuillInput.Localizable.Formik name={`options.${props.index}.criteria`} />
					)}
				</Box>
			</Flex>
		</SettingsRow>
	);
}

function CompetencyLevelSetSelector(props: { competency?: graphql.Competency }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [field, , { setValue }] = useField<graphql.CompetencyLevelSet | undefined>(
		'competencyLevelSet',
	);

	const [, , { setValue: setOptions }] = useField<graphql.CompetencyLevelOption[]>('options');

	const { data, loading, refetch } = graphql.useCompetencyLevelSetsQuery({
		variables: {
			isArchived: false,
			first: 100,
		},
		fetchPolicy: 'cache-and-network',
	});

	const route = (
		<Route path={[`/settings/competencies/:id/levels`, `/settings/competencies/new/levels`]} exact>
			<CompetencyLevelSetModal
				onSave={async (set) => {
					await refetch();

					if (!props.competency) {
						setLevelSet(set);
					}
				}}
				returnUrl={`/settings/competencies/${props.competency?.id ?? 'new'}`}
				set={props.competency && props.competency.competencyLevelSet}
			/>
		</Route>
	);

	if (loading) {
		return (
			<>
				{
					<Route path={`/settings/competencies/:id/levels`} exact>
						<CompetencyLevelSetModal
							onSave={async (set) => {
								await refetch();

								if (!props.competency) {
									setLevelSet(set);
								}
							}}
							returnUrl={`/settings/competencies/${props.competency?.id ?? 'new'}`}
							set={props.competency && props.competency.competencyLevelSet}
						/>
					</Route>
				}
				<Spinner />
			</>
		);
	}

	const setLevelSet = (set: graphql.CompetencyLevelSet) => {
		setValue(set);
		setOptions(
			set.levels.map((level) => ({
				level,
				enabled: true,
				criteria: [],
			})),
		);
	};

	const select = (
		<Select
			disabled={!!props.competency}
			small
			onChange={(value) => {
				const set = data?.competencyLevelSets.edges.find((edge) => edge.node.id === value)
					?.node as graphql.CompetencyLevelSet;

				if (!set) {
					return;
				}

				setLevelSet(set);
			}}
			selectedValue={field.value?.id}
			options={
				data?.competencyLevelSets.edges.map((edge) => ({
					title: localize(edge.node.title).text,
					value: edge.node.id,
				})) ?? []
			}
		/>
	);

	if (props.competency) {
		return (
			<>
				{route}
				<TooltipTarget title={t('common.settings.competencies.edit.readonly')}>
					{select}
				</TooltipTarget>
			</>
		);
	}

	return (
		<>
			{route}
			{select}
		</>
	);
}

function CompetencyLevelSetChooser() {
	const { t } = useTranslation();

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.group} width="100%">
			<Paragraph mb={0}>{t('common.settings.competencies.create.explanation')}</Paragraph>
			<Paragraph mb={0}>{t('common.settings.competencies.create.encouragement')}</Paragraph>
			<Flex alignItems="center">
				<Paragraph mb={0}>{t('common.settings.competencies.create.selectExisting')}</Paragraph>
				<Box width={200} mx={3}>
					<CompetencyLevelSetSelector />
				</Box>
				<Paragraph mb={0}>{t('common.settings.competencies.create.or')}</Paragraph>
				<Box mx={3}>
					<Button
						title={t('common.settings.competencies.create.createLevelSet')}
						type="secondary"
						link="/settings/competencies/new/levels"
						small
					/>
				</Box>
			</Flex>
		</FlexGap>
	);
}

function CompetencyEditor(props: { competency?: graphql.Competency }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { localize } = useLocalize();
	const hasGrowPath = useFeature(graphql.FeatureName.GrowPath);

	const useCompetencyGoals = useFeature(graphql.FeatureName.CompetencyGoals);

	const formik = useFormik({
		initialValues: {
			competencyLevelSet: props.competency?.competencyLevelSet,
			title: props.competency?.title ?? [],
			description: props.competency?.description ?? [],
			options:
				props.competency?.levels.map((level) => ({
					...level,
					criteria: level.criteria ?? [],
				})) ?? [],
			jobTitles: props.competency?.jobTitles.map((jobTitle) => jobTitle.id) ?? [],
			attachableToGoal: props.competency?.attachableToGoal ?? false,
			label: props.competency?.label?.id,
		},
		onSubmit: () => onUpdate(),
		validationSchema: validator.object({
			title: validator
				.array(
					validator.object({
						text: validator.string().min(1).max(256).required(),
					}),
				)
				.min(1),
		}),
	});

	const { data: jobTitlesData, loading: jobTitlesLoading } = graphql.useJobTitlesQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const [fetchLabels, { data: labelsData, error: labelsError }] =
		graphql.usePaginatedLabelsLazyQuery({
			variables: {
				isArchived: false,
				type: LabelType.Competency,
				first: 1000,
			},
		});

	const [createLabel, { error: createLabelError }] = graphql.useCreateLabelMutation();

	const submitLabel = async (value: string) => {
		await createLabel({
			variables: {
				input: {
					type: LabelType.Competency,
					title: [
						{
							languageID: localStorage.getItem('language') ?? 'nl',
							text: value,
						},
					],
				},
			},
		});
	};

	const updateLabel = (choice: { custom: string } | { value: string } | undefined) => {
		if (choice && 'value' in choice) {
			return formik.setFieldValue('label', choice.value);
		}

		if (choice && 'custom' in choice) {
			return submitLabel(choice.custom);
		}
	};

	const [error, attempt, reset] = useErrorBoundary();

	const [create, { loading: createLoading }] = graphql.useCreateCompetencyMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateCompetencyMutation();
	const loading = createLoading || updateLoading || jobTitlesLoading;

	const labelError = labelsError ?? createLabelError;

	const mappedLabels =
		labelsData?.paginatedLabels.edges.map((edge) => {
			return {
				title: edge.node.title[0].text,
				value: edge.node.id,
			};
		}) ?? [];

	const onUpdate = attempt(async () => {
		reset();

		const input = {
			title: formik.values.title.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			description: formik.values.description?.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			levels: formik.values.options.map((option) => ({
				competencyLevelID: option.level.id,
				enabled: option.enabled,
				criteria: option.criteria?.map((string) => ({
					languageID: string.languageID,
					text: string.text,
				})),
			})),
			jobTitleIDs: formik.values.jobTitles ?? [],
			attachableToGoal: formik.values.attachableToGoal,
			labelID: formik.values.label,
		};

		if (props.competency) {
			await update({
				variables: {
					input: {
						id: props.competency.id,
						...input,
					},
				},
			});
		} else {
			const { data } = await create({
				variables: {
					input: {
						competencyLevelSetID: formik.values.competencyLevelSet!.id,
						...input,
					},
				},
			});

			if (data) {
				history.push(`/settings/competencies/${data.createCompetency.competency.id}`);
			}
		}

		addToast(t('common.settings.competencies.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const history = useHistory();

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
			<Flex alignItems="center">
				<Heading mb={0}>
					{t(`common.settings.competencies.${props.competency ? 'edit' : 'create'}.title`)}
				</Heading>
				{props.competency?.styrSkillID && (
					<Box ml={2}>
						<StyrBadge />
					</Box>
				)}
			</Flex>
			<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
				<GraphQLErrorMessage error={error} />
				<FormikProvider value={formik}>
					<SettingsSection first title={t('common.settings.competencies.edit.details')}>
						<Form>
							<DetailsTableForm
								formValues={[
									{
										label: t('common.settings.competencies.edit.name'),
										name: 'title',
										type: DetailsTableFormValueTypes.Multilingual,
									},
									{
										label: t('common.settings.competencies.edit.description'),
										name: 'description',
										type: DetailsTableFormValueTypes.MultilingualQuill,
									},
									...(useCompetencyGoals
										? [
												{
													label: t('common.settings.competencies.edit.attachableToGoal'),
													name: 'attachableToGoal',
													type: DetailsTableFormValueTypes.Switch,
												},
											]
										: []),
									...(formik.values.attachableToGoal
										? [
												{
													label: t('common.settings.competencies.edit.jobTitles'),
													name: 'jobTitles',
													type: DetailsTableFormValueTypes.MultiSelect,
													options: jobTitlesData?.jobTitles.edges.map(({ node }) => ({
														value: node.id,
														label: localize(node.title)?.text,
													})),
												},
											]
										: []),
								]}
							/>
						</Form>
					</SettingsSection>

					{props.competency && props.competency.jobCompetencies.length > 0 && (
						<SettingsSection title={t('common.settings.competencies.edit.jobSpecificDescriptions')}>
							<Card p={0}>
								{props.competency.jobCompetencies.map((jobCompetency) => (
									<DescriptionRow key={jobCompetency.id}>
										<Flex alignItems="center" mb={2}>
											<JobTitleLink to={`/settings/jobs/titles/${jobCompetency.jobTitle.id}`}>
												<Heading3 mb={0}>{localize(jobCompetency.jobTitle.title).text}</Heading3>
											</JobTitleLink>
											{jobCompetency.jobTitle.styrData?.jobID && (
												<Box ml={2}>
													<StyrBadge />
												</Box>
											)}
										</Flex>
										<QuillText value={localize(jobCompetency.description ?? []).text} />
									</DescriptionRow>
								))}
							</Card>
						</SettingsSection>
					)}

					{hasGrowPath && (
						<SettingsSection
							title={t('common.settings.competencies.edit.label')}
							description={t('common.settings.competencies.edit.label.description')}
						>
							{labelError && <GraphQLErrorMessage error={labelError.graphQLErrors[0]} />}
							<Combo
								fetch={fetchLabels}
								{...(props.competency?.label
									? { preSelectedValue: localize(props.competency.label.title).text }
									: {})}
								onChange={(choice) => updateLabel(choice)}
								options={mappedLabels}
							/>
						</SettingsSection>
					)}

					{props.competency ? (
						<SettingsSection
							title={t('common.settings.competencies.edit.levels')}
							actions={
								<Flex alignItems="center">
									<Box width={200} mr={2}>
										<CompetencyLevelSetSelector competency={props.competency} />
									</Box>
									{formik.values.competencyLevelSet && (
										<Button
											onClick={() =>
												history.push(
													`/settings/competencies/${props.competency?.id ?? 'create'}/levels`,
												)
											}
											title={t('common.edit')}
											type="secondary"
										/>
									)}
								</Flex>
							}
						>
							<Box>
								{formik.values.options.map((option, index) => (
									<CompetencyLevel
										key={option.level.id}
										index={index}
										borderless={index === formik.values.options.length - 1}
										option={{
											...option,
											level: props.competency!.competencyLevelSet.levels[index],
										}}
									/>
								))}
							</Box>
						</SettingsSection>
					) : (
						<SettingsSection title={t('common.settings.competencies.edit.levels')}>
							<CompetencyLevelSetChooser />
						</SettingsSection>
					)}
				</FormikProvider>

				<Flex justifyContent="flex-end">
					<Button
						onClick={() => formik.submitForm()}
						loading={loading}
						disabled={!formik.values.competencyLevelSet}
						title={t('common.save')}
					/>
				</Flex>

				{props.competency && (
					<ActionArchiveListItem
						archivableItem={props.competency}
						archivableItemName="competency"
						archivableItemKey="competency"
						useArchive={graphql.useArchiveCompetencyMutation}
						useRestore={graphql.useRestoreCompetencyMutation}
					/>
				)}
			</FlexGap>
		</FlexGap>
	);
}

function CompetencyEditorID(props: { id: string }): ReactElement {
	const { data, error, loading } = graphql.useCompetencyQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return <Spinner centered />;
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <CompetencyEditor competency={data!.competency as graphql.Competency} />;
	}
}

export { CompetencyEditor as CompetencyCreator };

export default CompetencyEditorID;
